import type { AnalysisClient, Outcome, ViewThruBatchAnalysis } from '../viewThruBatchView/BatchAnalysis'
import { analysisCsvFilename } from '../viewThruBatchView/BatchAnalysis'
import { clientRejectionReasons, reasonLabel } from '../viewThruBatchView/analysisReasons'
import { csvEscape } from '../viewThruBatchView/shared'

/**
 * Where a batch client stands in Sentinel Reporting (SRDS):
 * - imported: their SRDS reference for this batch exists — imported from here.
 * - elsewhere: no reference match, but their email is in SRDS under another
 *   record (an earlier batch or a non-ViewThru route).
 * - not_imported: nothing in SRDS for them.
 */
export type ImportedStatus = 'imported' | 'elsewhere' | 'not_imported'

export const IMPORTED_STATUS_LABELS: Record<ImportedStatus, string> = {
    imported: 'Imported from this batch',
    elsewhere: 'In SRDS from elsewhere',
    not_imported: 'Not imported',
}

export interface SrdsAgreement {
    caseRef: string
    lender: string | null
    policyNumber: string | null
    value: number
    cancelled: boolean
    funded: boolean
}

/** The SRDS record a batch client matched. */
export interface SrdsRecord {
    clientId: number
    clientRef: string | null
    origin: string | null
    email: string
    name: string
    dob: string | null
    postcode: string | null
    importedAt: string | null
    optInDate: string | null
    leads: number
    leadsCancelled: number
    leadsFunded: number
    /** Total quantum of the live (not cancelled) agreements. */
    value: number
    agreements: SrdsAgreement[]
}

/** One client from the batch as the `viewthru-batch-imported` endpoint reports them. */
export interface ImportedClient {
    clientId: number
    clientRef: string
    name: string
    email: string
    dob: string | null
    postcode: string | null
    rowNumber: number | null
    entryId: number | null
    agreementCount: number
    status: ImportedStatus
    matchedOn: 'client_ref' | 'email' | null
    otherRecords: number
    srds: SrdsRecord | null
}

export interface ImportedMatchResponse {
    success: boolean
    batchId: string
    batch: { id: string; filename: string | null; origin: string | null; created_at: string | null }
    target: string
    counts: {
        clients: number
        imported: number
        elsewhere: number
        notImported: number
        importedLeads: number
        importedLeadsCancelled: number
        importedValue: number
    }
    clients: ImportedClient[]
}

/** A batch client with the analysis verdict alongside what SRDS holds for them. */
export interface ImportedReportRow extends ImportedClient {
    key: string
    /** What the eligibility analysis made of them, when it ran. */
    analysisOutcome: Outcome | null
    analysisReasons: string[]
    /** The analysed client, when known, so their record and PDFs can be opened. */
    client: AnalysisClient | null
}

export interface ImportedReport {
    rows: ImportedReportRow[]
    imported: ImportedReportRow[]
    elsewhere: ImportedReportRow[]
    notImported: ImportedReportRow[]
    totals: {
        importedLeads: number
        importedLeadsCancelled: number
        importedLeadsFunded: number
        importedValue: number
        /** Clients the analysis found eligible who are not in SRDS at all. */
        notImportedEligible: number
    }
}

const idKey = (value: number | string | null | undefined) =>
    value === null || value === undefined || value === '' ? null : String(value)

const byName = (a: ImportedReportRow, b: ImportedReportRow) =>
    a.name.localeCompare(b.name) || a.email.localeCompare(b.email)

/**
 * Pairs the SRDS match for each batch client with the analysis verdict, so the
 * report can show not just who was imported but whether anyone who should have
 * been is missing. The analysis is optional: without it the SRDS facts stand
 * on their own.
 */
export const buildImportedReport = ({
    matches,
    analysis,
}: {
    matches: ImportedMatchResponse
    analysis: ViewThruBatchAnalysis | null
}): ImportedReport => {
    const outcomeById = new Map<string, { client: AnalysisClient; outcome: Outcome }>()
    if (analysis) {
        const tag = (clients: AnalysisClient[] | undefined, outcome: Outcome) => {
            for (const client of clients || []) {
                const id = idKey(client.introducerClientId)
                if (id && !outcomeById.has(id)) outcomeById.set(id, { client, outcome })
            }
        }
        tag(analysis.eligibleClients, 'eligible')
        tag(analysis.ineligibleClients, 'ineligible')
        tag(analysis.duplicateClients || analysis.notEligibleDups, 'duplicate')
    }

    const rows: ImportedReportRow[] = (matches.clients || []).map((client) => {
        const analysed = outcomeById.get(String(client.clientId)) || null
        return {
            ...client,
            key: `client:${client.clientId}`,
            analysisOutcome: analysed?.outcome ?? null,
            analysisReasons:
                analysed?.outcome === 'ineligible' ? clientRejectionReasons(analysed.client).map(reasonLabel) : [],
            client: analysed?.client ?? null,
        }
    })

    const imported = rows.filter((row) => row.status === 'imported').sort(byName)
    const elsewhere = rows.filter((row) => row.status === 'elsewhere').sort(byName)
    const notImported = rows.filter((row) => row.status === 'not_imported').sort(byName)

    return {
        rows: [...imported, ...elsewhere, ...notImported],
        imported,
        elsewhere,
        notImported,
        totals: {
            importedLeads: imported.reduce((sum, row) => sum + (row.srds?.leads || 0), 0),
            importedLeadsCancelled: imported.reduce((sum, row) => sum + (row.srds?.leadsCancelled || 0), 0),
            importedLeadsFunded: imported.reduce((sum, row) => sum + (row.srds?.leadsFunded || 0), 0),
            importedValue: imported.reduce((sum, row) => sum + (row.srds?.value || 0), 0),
            notImportedEligible: notImported.filter((row) => row.analysisOutcome === 'eligible').length,
        },
    }
}

export type ImportedScope = 'all' | ImportedStatus

/** Imported first: that list is the report; the rest is supporting detail. */
export const IMPORTED_SCOPES: { scope: ImportedScope; label: string; suffix: string }[] = [
    { scope: 'imported', label: 'Imported from this batch', suffix: 'imported' },
    { scope: 'elsewhere', label: 'In SRDS from elsewhere', suffix: 'in-srds-elsewhere' },
    { scope: 'not_imported', label: 'Not imported', suffix: 'not-imported' },
    { scope: 'all', label: 'Everyone', suffix: 'full' },
]

export const filterImportedRows = (report: ImportedReport, scope: ImportedScope) =>
    scope === 'all'
        ? report.rows
        : scope === 'imported'
        ? report.imported
        : scope === 'elsewhere'
        ? report.elsewhere
        : report.notImported

export interface ImportedCsvContext {
    batchId: string
    batchLabel: string
    batchOrigin: string
    ranAt: Date
}

const csvMoney = (value: number | null | undefined) =>
    typeof value === 'number' && Number.isFinite(value) ? value.toFixed(2) : ''

const joinList = (values: string[]) => values.filter(Boolean).join(' | ')

const OUTCOME_TEXT: Record<Outcome, string> = { eligible: 'Eligible', ineligible: 'Not eligible', duplicate: 'Duplicate' }

const IMPORTED_CSV_COLUMNS: {
    header: string
    value: (row: ImportedReportRow, context: ImportedCsvContext) => string | number | null
}[] = [
    { header: 'batch_file', value: (_row, context) => context.batchLabel },
    { header: 'batch_origin', value: (_row, context) => context.batchOrigin },
    { header: 'batch_id', value: (_row, context) => context.batchId },
    { header: 'report_date', value: (_row, context) => context.ranAt.toISOString() },
    { header: 'status', value: (row) => IMPORTED_STATUS_LABELS[row.status] },
    { header: 'matched_on', value: (row) => (row.matchedOn === 'client_ref' ? 'client reference' : row.matchedOn || '') },
    { header: 'client_id', value: (row) => row.clientId },
    { header: 'client_ref', value: (row) => row.clientRef },
    { header: 'name', value: (row) => row.name },
    { header: 'email', value: (row) => row.email },
    { header: 'dob', value: (row) => row.dob || '' },
    { header: 'postcode', value: (row) => row.postcode || '' },
    { header: 'agreements_in_batch', value: (row) => row.agreementCount },
    { header: 'srds_client_id', value: (row) => row.srds?.clientId ?? '' },
    { header: 'srds_client_ref', value: (row) => row.srds?.clientRef || '' },
    { header: 'srds_origin', value: (row) => row.srds?.origin || '' },
    { header: 'imported_at', value: (row) => row.srds?.importedAt || '' },
    { header: 'agreements_imported', value: (row) => (row.srds ? row.srds.leads : '') },
    { header: 'agreements_cancelled', value: (row) => (row.srds ? row.srds.leadsCancelled : '') },
    { header: 'agreements_funded', value: (row) => (row.srds ? row.srds.leadsFunded : '') },
    { header: 'value_imported', value: (row) => (row.srds ? csvMoney(row.srds.value) : '') },
    {
        header: 'imported_agreements',
        value: (row) =>
            joinList(
                (row.srds?.agreements || []).map(
                    (lead) =>
                        `${lead.caseRef}${lead.lender ? ` (${lead.lender})` : ''}=${csvMoney(lead.value)}${
                            lead.cancelled ? ' cancelled' : ''
                        }`
                )
            ),
    },
    { header: 'other_srds_records', value: (row) => row.otherRecords },
    { header: 'analysis_outcome', value: (row) => (row.analysisOutcome ? OUTCOME_TEXT[row.analysisOutcome] : '') },
    { header: 'analysis_reasons', value: (row) => joinList(row.analysisReasons) },
]

export const buildImportedReportCsv = (rows: ImportedReportRow[], context: ImportedCsvContext) =>
    [
        IMPORTED_CSV_COLUMNS.map((column) => csvEscape(column.header)).join(','),
        ...rows.map((row) => IMPORTED_CSV_COLUMNS.map((column) => csvEscape(column.value(row, context))).join(',')),
    ].join('\n')

export const importedReportFilename = (batchLabel: string, scope: ImportedScope, ranAt: Date) => {
    const suffix = IMPORTED_SCOPES.find((entry) => entry.scope === scope)?.suffix || scope
    return analysisCsvFilename(batchLabel, `srds-${suffix}-${ranAt.toISOString().slice(0, 10)}`)
}
